import moment from "moment";

import { Client } from "@/Common/Api/Client";
import { IndividualApiClient } from "@/Common/Api/Individuals/IndividualApiClient";
import { FlatLookupView } from "@/Common/Api/MedfarDictionaries/Dto/Responses/FlatLookupView";
import { ValidationRule } from "@/Common/Api/Validation/Dto/Responses/ValidationRule";
import { ButtonControl } from "@/Common/Components/Buttons/ButtonControl";
import { AddressControl, AddressControlDTO } from "@/Common/Components/Contacts/AddressControl";
import { CheckboxControl } from "@/Common/Components/Inputs/CheckboxControl";
import { DateSelector } from "@/Common/Components/Inputs/DateSelector";
import { RadioButton } from "@/Common/Components/Inputs/RadioButton";
import { TextboxControl } from "@/Common/Components/Inputs/TextboxControl";
import { TextboxMaskedControl } from "@/Common/Components/Inputs/TextboxMaskedControl";
import { HealthCardComponent } from "@/Common/Components/Patient/HealthCardComponents/HealthCardComponent";
import { HealthCardValidatorFactory, HealthCardValidatorRequest } from "@/Common/Components/Patient/HealthCardComponents/HealthCardValidator";
import { IndividualAdvancedSearchControl } from "@/Common/Components/Search/IndividualAdvancedSearchControl";
import { EnumHelper } from "@/Common/Helpers/EnumHelper";
import { IndividualIdentityHelper } from "@/Common/Helpers/IndividualIdentityHelper";
import { PatientHelper } from "@/Common/Helpers/PatientHelper";
import { ValidationHelper } from "@/Common/Helpers/ValidationHelper";
import { PhoneType } from "@/Common/Models/Contacts/Enums/PhoneType";
import { GenderIdentity } from "@/Common/Models/Individuals/Enums/GenderIdentity";
import { PreferredPronouns } from "@/Common/Models/Individuals/Enums/PreferredPronouns";
import { Person } from "@/Common/Models/Person/Person";
import { RAMQInfo } from "@/Common/Models/RAMQ/RAMQInfo";
import { KeyValuePair } from "@/Common/Models/UI/KeyValuePair";
import * as ServerOperations from "@/Common/Operations/operations.server";
import { IRelativeCreationModel, RelativeCreationModel } from "@/Relative/Models/UI/CreateRelativeUIModel";
import { Relative } from "../Models/Relative";

import styles from "./CreateRelative.module.scss";

import img_loading from "@/Globals/_images/loading.gif";

interface ICreateRelativeProps {
    onChange: (relative: Relative, isFormValid: boolean) => void;
    onDuplicateSelect: (person: Person) => void;
}

class CreateRelativeState {
    public relative: IRelativeCreationModel;
    public isLoadingRAMQ: boolean;
    public isNAMValid: boolean;
    public createInProgress: boolean;
    public displayDuplicatePopup: boolean;
    public duplicateCreatedIndividualContactIds: string[];

    public constructor(init?: Partial<CreateRelativeState>) {
        Object.assign(this, init);
    }
}

class CreateRelative extends React.Component<ICreateRelativeProps, CreateRelativeState> {
    private readonly validationHelper = new ValidationHelper();
    private readonly maleLookupId = cacheStore.FindLookupByLookupCode("MALE");
    private readonly malePrefixLookupId = cacheStore.FindLookupByLookupCode("PREFIX.MR");
    private readonly femaleLookupId = cacheStore.FindLookupByLookupCode("FEMALE");
    private readonly femalePrefixLookupId = cacheStore.FindLookupByLookupCode("PREFIX.MS");
    private readonly mexicoCountryLookup = cacheStore.FindLookupByLookupCode("CNMEXICO");
    private readonly cellphoneLookupId = cacheStore.getLookupByPhoneType(PhoneType.Mobile).LookupID;

    private id: string;
    private alreadyResolvedNam: string = "";
    private alreadyDuplicateCheckedNam: string;

    public constructor(props: ICreateRelativeProps) {
        super(props);
        this.id = sessionStore.newId().toString();
        this.state = new CreateRelativeState({
            relative: RelativeCreationModel.getDefaultImmutable(),
            createInProgress: false
        });
    }

    public shouldComponentUpdate(nextProps: ICreateRelativeProps, nextState: CreateRelativeState): boolean {
        const isRelativeUpdated = !(nextState.relative == this.state.relative);
        const isCreateInProgress = !(nextState.createInProgress == this.state.createInProgress);
        const isLoadingRAMQ = !(nextState.isLoadingRAMQ == this.state.isLoadingRAMQ);
        const isDisplayDuplicatePopup = !(nextState.displayDuplicatePopup == this.state.displayDuplicatePopup);

        return isRelativeUpdated || isCreateInProgress || isLoadingRAMQ || isDisplayDuplicatePopup;
    }

    public mutateRelativeModel(mutation: (model: IRelativeCreationModel) => IRelativeCreationModel, then: () => void = null): void {
        this.mutateState(s => s.relative = mutation(this.state.relative), () => {
            this.props.onChange(this.parseToRelative(), this.isFormValid());
            then?.();
        });
    }

    public render(): JSX.Element {
        const initialCountryLookupId: string = this.state.relative?.get("SelectedCountryLookupId") || cacheStore.GetDefaultCountryForActiveSite().LookupID;
        const validatorCountryLookupId = cacheStore.ClinicSettings.EnableHealthCardValidation
            ? cacheStore.GetDefaultCountryForActiveSite().LookupID
            : initialCountryLookupId;

        const phonevalidationRule = this.validationHelper.getPhoneNumberValidationRule(initialCountryLookupId, null);
        const phonemask: string = phonevalidationRule ? phonevalidationRule.EditMaskPattern : "";

        const phonevalidationErrorMessage: string = (phonevalidationRule && phonevalidationRule.ValidationErrorMessageLookupId) ?
            cacheStore.GetLocalizedValueByLookupId(phonevalidationRule.ValidationErrorMessageLookupId) : "";
        const primaryPhoneTypeLookupId: string = this.state.relative?.get("Phone1TypeLookupId");
        const primaryPhoneContactLookupId: string = this.state.relative?.get("Phone1ContactTypeLookupId");
        const primaryPhoneValue = `${primaryPhoneContactLookupId}_${primaryPhoneTypeLookupId}`;
        const secondaryPhoneTypeLookupId: string = this.state.relative?.get("Phone2TypeLookupId");
        const secondaryPhoneContactLookupId: string = this.state.relative?.get("Phone2ContactTypeLookupId");
        const secondaryPhoneValue = `${secondaryPhoneContactLookupId}_${secondaryPhoneTypeLookupId}`;

        const healthCardValidator = HealthCardValidatorFactory.getValidator(
            validatorCountryLookupId,
            RelativeCreationModel.getHealthCardRegionLookupId(this.state.relative)
        );

        return (
            <div className={styles.createRelative}>
                {this.state.displayDuplicatePopup && <div className="fixed-overlay" />}
                <div className={styles.createRelativeContent}>
                    {this.state.displayDuplicatePopup &&
                        this.renderDuplicatePopup()
                    }

                    {this.renderRow(
                        healthCardValidator.healthCardLabel,
                        this.healthCardSection(),
                        false,
                        this.state.isLoadingRAMQ
                    )}

                    {this.renderRow(
                        cacheStore.GetString("PrefixLabel"),
                        <select
                            onChange={e => this.onPrefixChanged(e)}
                            className="form-control myle-control"
                            value={this.state.relative.get("SelectedPrefixLookupId")}
                        >
                            <option value="" />
                            {cacheStore.GetPrefixIndividualLookups().map((p: FlatLookupView) => (
                                <option key={p.LookupID} value={p.LookupID}>
                                    {cacheStore.GetLocalizedValueByLookupId(p.LookupID)}
                                </option>
                            ))}
                        </select>
                    )}

                    {this.renderRow(
                        cacheStore.GetString("LastNameLabel"),
                        <TextboxControl
                            className="form-control"
                            value={this.state.relative.get("LastName")}
                            valid={!!this.state.relative.get("LastName")}
                            onChange={textOrDate => this.onLastNameChange(textOrDate)}
                            dataCy="createRelative-lastName"
                        />,
                        true
                    )}

                    {this.renderRow(
                        cacheStore.GetString("FirstNameLabel"),
                        <TextboxControl
                            className="form-control"
                            value={this.state.relative.get("FirstName")}
                            valid={!!this.state.relative.get("FirstName")}
                            onChange={textOrDate => this.onFirstNameChange(textOrDate)}
                            dataCy="createRelative-firstName"
                        />,
                        true
                    )}

                    {this.renderRow(
                        cacheStore.GetString("PreferredNameLabel"),
                        <TextboxControl
                            className="form-control"
                            value={this.state.relative.get("PreferredName")}
                            onChange={e => this.onPreferredNameChange(e)}
                        />
                    )}

                    {this.renderRow(
                        cacheStore.GetString("DateofBirthLabel"),
                        this.dateOfBirthAndGenderSection("SexAtBirthLabel")
                    )}

                    {this.renderRow(
                        cacheStore.GetString("GenderIdentityLabel"),
                        <select
                            onChange={e => this.onGenderIdentityChange(e)}
                            className="form-control myle-control"
                            value={EnumHelper.getName(GenderIdentity, this.state.relative.get("GenderIdentity"))}
                        >
                            <option />
                            {IndividualIdentityHelper.getAllGenderIdentitiesAsHtmlOption()}
                        </select>
                    )}

                    {this.renderRow(
                        cacheStore.GetString("PreferredPronounsLabel"),
                        <select
                            onChange={e => this.onPreferredPronounsChange(e)}
                            className="form-control myle-control"
                            value={EnumHelper.getName(PreferredPronouns, this.state.relative.get("PreferredPronouns"))}
                        >
                            {IndividualIdentityHelper.getAllPreferredPronounsAsHtmlOption()}
                        </select>
                    )}

                    {this.state.relative.get("PreferredPronouns") === PreferredPronouns.Other && (
                        this.renderRow(
                            "",
                            <TextboxControl
                                className="form-control"
                                value={this.state.relative.get("SpecifiedPreferredPronouns")}
                                valid={true}
                                onChange={value => this.onSpecifiedPreferredPronounsChange(value)}
                            />
                        )
                    )}

                    {this.adressSection(initialCountryLookupId)}

                    {/* PHONE 1 */}
                    {this.renderRow(
                        <select
                            id="primaryPhoneType"
                            key="primaryPhoneType"
                            onChange={(e: any) => this.onPhone1TypeChange(e.target.value)}
                            className="form-control background-transparent selectable-label"
                            value={primaryPhoneValue}
                        >
                            {this.phoneContactTypeOptions()}
                        </select>,
                        this.numberSection(primaryPhoneTypeLookupId, phonemask, phonevalidationErrorMessage, true)
                    )}

                    {/* PHONE 2 */}
                    {this.renderRow(
                        <select
                            id="secondaryPhoneType"
                            key="secondaryPhoneType"
                            onChange={(e: any) => this.onPhone2TypeChange(e.target.value)}
                            className="form-control background-transparent selectable-label"
                            value={secondaryPhoneValue}
                        >
                            {this.phoneContactTypeOptions()}
                        </select>,
                        this.numberSection(secondaryPhoneTypeLookupId, phonemask, phonevalidationErrorMessage, false)
                    )}

                    {/* Email */}
                    {this.renderRow(
                        this.emailSelect(),
                        this.emailField()
                    )}
                </div>
            </div>
        );
    }

    private renderDuplicatePopup(): JSX.Element {
        return (
            <div className={styles.duplicatePopup}>
                <div className={styles.duplicatePopupContent}>
                    <h3>{cacheStore.GetString("DuplicateTitleLabel")}</h3>
                    <strong>{cacheStore.GetString("DuplicateSentence1Label")}</strong>
                    <div> {cacheStore.GetString("SelectRecordOrContinueExternalRelationCreation")} </div>

                    <div className="patient-sidebar-search-container full-size">
                        <IndividualAdvancedSearchControl
                            defaultTextValue="xxx"
                            isFocusOnLoad
                            componentId="_patientAdvancedSearchDuplicate"
                            maxResultsToReturn={8}
                            onIndividualSelectCallback={person => this.onIndivualSelect(person)}
                            specificContactIdsToDisplay={this.state.duplicateCreatedIndividualContactIds}
                        />
                    </div>
                </div>
                <div className={styles.duplicatePopupFooter}>
                    <ButtonControl
                        variant="neutral"
                        onClick={() => this.onCloseDuplicatePopup()}
                        label={cacheStore.GetString("BackLabel")}
                    />
                </div>
            </div>
        );
    }

    private onIndivualSelect(person: Person): void {
        if (person.NAM === sessionStore.selectedFile.NAM)
            return;
        this.props.onDuplicateSelect(person);
        this.onCloseDuplicatePopup();
    }

    private onCloseDuplicatePopup(): void {
        let s = new CreateRelativeState();
        s.displayDuplicatePopup = false;
        this.setState(s);
    }

    private renderRow(label: string | JSX.Element, field: JSX.Element, isRequired?: boolean, showLoading?: boolean): JSX.Element {
        return (
            <div className="row">
                <div className="col-xs-4 text-right">
                    {showLoading && (
                        <span className="label-loading">
                            <img
                                className={styles.loadingImg}
                                src={img_loading}
                                alt="loading"
                            />
                        </span>
                    )}
                    {isRequired && (
                        <span className={`label-error bold ${styles.mandatoryRow}`}>
                            *
                        </span>
                    )}
                    <span className="form-label">{label}</span>
                </div>
                <div className="col-xs-8">
                    {field}
                </div>
            </div>
        );
    }

    private adressSection(initialCountryLookupId: string): JSX.Element {
        const initialStateLookupId: string = this.state.relative.get("SelectedRegionLookupId") || cacheStore.GetDefaultRegionForActiveSite().LookupID;
        const initialStreetAddress: string = this.state.relative.get("CivicAddressEdit");
        const initialCity: string = this.state.relative.get("CityEdit");
        const initialPostalCode: string = this.state.relative.get("PostalCodeEdit");

        return (
            <AddressControl
                id="createRelative_AdressControl"
                initialStreetAddress={initialStreetAddress}
                initialCity={initialCity}
                initialPostalCode={initialPostalCode}
                initialCountryLookupId={initialCountryLookupId}
                initialStateLookupId={initialStateLookupId}
                onNotifyParentCallback={dto => this.onAddressSelect(dto)}
                rowClassName="row margin-sm-bottom"
                labelClassName="col-xs-4 text-right"
                bodyClassName="col-xs-8"
            />
        );
    }

    private healthCardSection(): JSX.Element {
        const isSelectedProvinceOntario =
            this.state.relative.get("SelectedRegionLookupId") == cacheStore.FindLookupByLookupCode("CanadaState_ON").LookupID;
        const isMexicoCURP = this.state.relative.get("SelectedRegionLookupId") == this.mexicoCountryLookup.LookupID;

        const healthIdentificationValidationRule = this.validationHelper.getHealthcardValidationRule(
            this.state.relative.get("SelectedRegionLookupId"),
            !isMexicoCURP ? this.state.relative.get("SelectedRegionLookupId") : null
        );
        const healthIdentificationValidationErrorMessage: string = cacheStore.GetString("InvalidLabel");
        let healthIdentificationMask = healthIdentificationValidationRule?.EditMaskPattern;

        if (isSelectedProvinceOntario && !cacheStore.ClinicSettings.EnableHealthCardValidation)
            healthIdentificationMask = "";

        return (
            <div className={styles.healthCardSection}>
                <div className={styles.healthCardField}>
                    {cacheStore.ClinicSettings.EnableHealthCardValidation
                        ? (
                            <HealthCardComponent
                                healthCard={this.state.relative.get("NAMEdit")}
                                expiryDate={this.state.relative.get("NAMExpiryEdit")}
                                regionLookupId={this.state.relative.get("SelectedHealthCardRegionLookupId")}
                                countryLookupId={cacheStore.GetDefaultCountryForActiveSite().LookupID}
                                isValid={this.state.relative.get("IsNAMValid")}
                                onHealthCardNumberChanged={healthCard => this.onNAMChange(healthCard, null)}
                                onExpiryDateChanged={expiryDate => this.onNAMExpiryChange(expiryDate)}
                                onRegionLookupIdChanged={regionLookupId => this.handleOnHealthCardRegionChange(regionLookupId)}
                            />
                        )
                        : (
                            <TextboxMaskedControl
                                isValid={this.state.relative.get("IsNAMValid")}
                                focus
                                id={`NAMEdit_${this.id}`}
                                value={this.state.relative.get("NAMEdit") ?? ""}
                                onChangeFunction={this.onNAMChange.bind(this)}
                                mask={healthIdentificationMask}
                                validationErrorMessage={healthIdentificationValidationErrorMessage}
                            />
                        )
                    }

                </div>
                {!isMexicoCURP && !cacheStore.ClinicSettings.EnableHealthCardValidation && (
                    <div className={styles.healthCardDatePicker}>
                        <DateSelector
                            id="NAMExpiry_Date_Relative_Creation"
                            width={130}
                            cssClass="rounded"
                            date={this.state.relative.get("NAMExpiryEdit")}
                            onChangeCallback={date => this.onNAMExpiryChange(date)}
                            dateFormat="yyyy MMMM"
                            disabled={false}
                            isMonthPicker
                        />
                    </div>
                )}
            </div>
        );
    }

    private dateOfBirthAndGenderSection(labelName: string): JSX.Element {
        return (
            <div className={styles.birthdateAndGenderSection}>
                <DateSelector
                    id={`DOBChange_${this.id}`}
                    cssClass="rounded"
                    width={140}
                    date={this.state.relative.get("DOB")}
                    onChangeCallback={this.onDOBChange.bind(this)}
                    onSelectOp={null}
                    dateFormat="yyyy/MM/dd"
                />

                <div className={styles.genderSelection}>
                    <label>{cacheStore.GetString(labelName)}</label>
                    <span>
                        <RadioButton
                            mainDivCSS=""
                            items={Immutable.List([
                                new KeyValuePair(
                                    cacheStore.GetLocalizedValueByLookupId(this.maleLookupId.LookupID).substr(0, 1),
                                    this.maleLookupId.LookupID
                                ),
                                new KeyValuePair(
                                    cacheStore.GetLocalizedValueByLookupId(this.femaleLookupId.LookupID).substr(0, 1),
                                    this.femaleLookupId.LookupID
                                )
                            ])}
                            radioInputGroupId="language"
                            onePerLine={false}
                            changeOperation={null}
                            onChange={item => this.onGenderChange(item.Value)}
                            selectedItem={this.state.relative.get("GenderLookupId")}
                        />
                    </span>
                </div>
            </div>
        );
    }

    private numberSection(lookupId: string, mask: string, validationErrorMessage: string, isPrimary: boolean): JSX.Element {
        const showPhoneExt: boolean = lookupId == cacheStore.getLookupByPhoneType(PhoneType.Phone).LookupID;
        const showPhoneNotification: boolean = lookupId === this.cellphoneLookupId;

        const phoneNumber = isPrimary ? this.state.relative.get("Phone1Edit") : this.state.relative.get("Phone2Edit");
        const phoneLabel: string = isPrimary ? "Phone1" : "Phone2";
        const isPhoneValid: boolean = isPrimary ? this.state.relative.get("IsPhone1Valid") : this.state.relative.get("IsPhone2Valid");
        const isNotificationsEnabled: boolean = isPrimary ? this.state.relative.get("Phone1NotificationsEnabled") : this.state.relative.get("Phone2NotificationsEnabled");
        const getPhoneExt: string = isPrimary ? this.state.relative.get("Phone1Extension") : this.state.relative.get("Phone2Extension");

        return (
            <div className={styles.numberSection}>
                <div className={styles.numberField}>
                    <TextboxMaskedControl
                        isValid={isPhoneValid}
                        id={`${phoneLabel}_${this.id}`}
                        value={phoneNumber}
                        onChangeFunction={val => isPrimary ?
                            this.mutateRelativeModel(s => s.set("Phone1Edit", val).set("IsPhone1Valid", this.isPhoneNumberValid(val)))
                            : this.mutateRelativeModel(s => s.set("Phone2Edit", val).set("IsPhone2Valid", this.isPhoneNumberValid(val)))
                        }
                        mask={mask}
                        validationErrorMessage={validationErrorMessage}
                        disabled={this.state.createInProgress}
                    />
                </div>

                <div className={styles.numberExtAndNotifsSubsection}>
                    {showPhoneExt && (
                        <div>
                            <span>{cacheStore.GetString("ExtLabel")}</span>
                            <div className={styles.numberExtensionField}>
                                <TextboxMaskedControl
                                    id={`${phoneLabel}_Ext`}
                                    value={getPhoneExt ?? ""}
                                    onChangeFunction={val => isPrimary ? this.mutateRelativeModel(s => s.set("Phone1Extension", val)) :
                                        this.mutateRelativeModel(s => s.set("Phone2Extension", val))
                                    }
                                    mask="[9{1,7}"
                                    validationErrorMessage={validationErrorMessage}
                                    isValid
                                />
                            </div>
                        </div>
                    )}
                    {!showPhoneExt && showPhoneNotification && (
                        <CheckboxControl
                            checked={isNotificationsEnabled}
                            label={cacheStore.GetString("NotifyPatientLabel")}
                            labelStyle={{ margin: "0" }}
                            onChange={checked => isPrimary ? this.mutateRelativeModel(s => s.set("Phone1NotificationsEnabled", checked)) :
                                this.mutateRelativeModel(s => s.set("Phone2NotificationsEnabled", checked))
                            }
                        />
                    )}
                </div>
            </div>
        );
    }

    private phoneContactTypeOptions(): JSX.Element[] {
        return cacheStore.GetPhoneContactPointTypeLookupTuples().map((phonetypeLookup: [FlatLookupView, FlatLookupView]) => {
            const contactType = phonetypeLookup[0];
            const phoneType = phonetypeLookup[1];

            const localizedContactType = cacheStore.GetLocalizedValueByLookupId(contactType.LookupID);
            const localizedPhoneType = cacheStore.GetLocalizedValueByLookupId(phoneType.LookupID);

            const id = `${contactType.LookupID}_${phoneType.LookupID}`;
            const shortValue = `${localizedContactType.substr(0, 1)} - ${localizedPhoneType}`;
            const longValue = `${localizedContactType} - ${localizedPhoneType}`;

            return <option key={id} value={id} title={longValue}>{shortValue}</option>;
        });
    }

    private emailSelect(): JSX.Element {
        const emailTypeValue: string = this.state.relative.get("EmailTypeByLookup");
        const emailContactPointTypeLookups: JSX.Element[] = cacheStore.GetEmailContactPointTypeLookups()
            .map((z: FlatLookupView) => {
                let longValue = cacheStore.GetLocalizedValueByLookupId(z.LookupID);
                return <option key={z.LookupID} value={z.LookupID} title={longValue}> @-  {longValue}</option>;
            });

        return (
            <select
                id="EmailType"
                key="EmailType"
                className="form-control background-transparent selectable-label"
                onChange={(e: any) => {
                    const val = e.target.value;
                    this.mutateRelativeModel(s => s.set("EmailTypeByLookup", val));
                }}
                value={emailTypeValue}
            >{emailContactPointTypeLookups}
            </select>
        );
    }

    private emailField(): JSX.Element {
        const validationRule = this.validationHelper.getEmailValidationRule();
        const emailMask: string = validationRule ? validationRule.EditMaskPattern : "";

        return (
            <div className={styles.emailSection}>
                <div className={styles.emailFieldReduced}>
                    <TextboxMaskedControl
                        isValid={this.state.relative.get("IsEmailValid")}
                        alphanumericIncludesPeriod={false}
                        onChangeOperation={null}
                        id={"Email" + this.id}
                        value={this.state.relative.get("EmailEdit") ?? ""}
                        onChangeFunction={val => this.onEmailChange(val)}
                        returnMaskedValue={true}
                        mask={emailMask}
                        disabled={this.state.createInProgress}
                        isEmail={true}
                    />
                </div>
                <div className={styles.emailNotification}>
                    <CheckboxControl
                        checked={this.state.relative.get("EmailNotificationsEnabled")}
                        label={cacheStore.GetString("NotifyPatientLabel")}
                        labelStyle={{ margin: "0" }}
                        onChange={checked =>
                            this.mutateRelativeModel(s => s.set("EmailNotificationsEnabled", checked))
                        }
                    />
                </div>
            </div>
        );
    }

    private mutateState(mutation: (s: CreateRelativeState) => void, then: any = null): void {
        this.setState(
            s => {
                const newState = { ...s as CreateRelativeState };
                mutation(newState);
                return newState;
            },
            then
        );
    }

    /**
     * @param unmaskedvalue        raw NAM value
     * @param maskedvalue          value with mask (ie LADP83080___ if missing chars)
     * @param isDirectModification set to true if event source comes directly from NAM control, false if this is called from other inputs that have an impact on NAM (ie firstname,lastname,dob)
     *                             this is to deal with triggering duplicateCheck validation after it has been dismissed ('continue with creation' clicked). If it is shown and dismissed,
                                   we do not want to show it again unless the nam has been directly edited again
     */
    private async onNAMChange(unmaskedvalue: string, maskedvalue: string, isDirectModification: boolean = true): Promise<void> {
        let s = new CreateRelativeState();
        s.relative = this.state.relative;

        const healthCardRegionLookupId = RelativeCreationModel.getHealthCardRegionLookupId(s.relative);
        const resolvedBirthDate = PatientHelper.getDateOfBirthFromNAM(unmaskedvalue, healthCardRegionLookupId);

        if (resolvedBirthDate && !s.relative.get("DOB"))
            s.relative = s.relative.set("DOB", resolvedBirthDate);

        s.relative = s.relative.set("NAMEdit", unmaskedvalue);
        if (isDirectModification || !s.relative.get("GenderLookupId"))
            s.relative = this.setGenderFromNAM(unmaskedvalue, s.relative);

        s.relative = s.relative.set("IsNAMValid", this.isHealthCardValid(unmaskedvalue, healthCardRegionLookupId));

        if (!(unmaskedvalue == null) && unmaskedvalue.length == 0) {
            s.relative = s.relative.set("NAMExpiryEdit", null);
            s.relative = s.relative.set("IsNAMValid", true);
        }
        else {
            if (!s.relative.get("NAMExpiryEdit")) {
                s.relative = s.relative.set("NAMExpiryEdit", moment().startOf("month").toDate());
            }
        }

        if (s.relative.get("IsNAMValid") === true && s.relative.get("NAMEdit"))
            await this.validateIsNotDuplicate(s.relative.get("NAMEdit"), healthCardRegionLookupId);

        this.setState(s, () => this.props.onChange(this.parseToRelative(), this.isFormValid()));
    }

    private handleOnHealthCardRegionChange(regionLookupId: string): void {
        this.mutateRelativeModel(
            s => s
                .set("SelectedHealthCardRegionLookupId", regionLookupId)
                .set("IsNAMValid", this.isHealthCardValid(s.get("NAMEdit"), regionLookupId)),
            () => {
                if (this.state.relative.get("IsNAMValid") === true && this.state.relative.get("NAMEdit"))
                    this.validateIsNotDuplicate(this.state.relative.get("NAMEdit"), RelativeCreationModel.getHealthCardRegionLookupId(this.state.relative));
            }
        );
    }

    private onNAMExpiryChange(val: Date): void {
        this.mutateRelativeModel(s => s.set("NAMExpiryEdit", val));
    }

    private onPrefixChanged(e: React.ChangeEvent<HTMLSelectElement>): void {
        this.mutateRelativeModel(s => s.set("SelectedPrefixLookupId", e.target.value));
    }

    private onLastNameChange(val: string): void {
        // revalidateNAM when state has been set with new last name value
        this.mutateRelativeModel(s => s.set("LastName", val), () => this.onNAMChange(this.state.relative.get("NAMEdit"), null, false));
    }

    private onFirstNameChange(val: string): void {
        // revalidateNAM when state has been set with new last name value
        this.mutateRelativeModel(s => s.set("FirstName", val), () => this.onNAMChange(this.state.relative.get("NAMEdit"), null, false));
    }

    private onPreferredNameChange(val: string): void {
        this.mutateRelativeModel(s => s.set("PreferredName", val));
    }

    private onGenderIdentityChange(e: React.ChangeEvent<HTMLSelectElement>): void {
        const genderIdentity = EnumHelper.getValue(GenderIdentity, e.target.value);
        this.mutateRelativeModel(s => s.set("GenderIdentity", genderIdentity));
    }

    private onPreferredPronounsChange(e: React.ChangeEvent<HTMLSelectElement>): void {
        const preferredPronouns = EnumHelper.getValue(PreferredPronouns, e.target.value);
        this.mutateRelativeModel(s => s
            .set("PreferredPronouns", preferredPronouns)
            .set("SpecifiedPreferredPronouns", preferredPronouns !== PreferredPronouns.Other && null)
        );
    }

    private onSpecifiedPreferredPronounsChange(val: string): void {
        this.mutateRelativeModel(s => s.set("SpecifiedPreferredPronouns", val));
    }

    private onDOBChange(val: Date): void {
        this.mutateRelativeModel(s => s.set("DOB", val), () => this.onNAMChange(this.state.relative.get("NAMEdit"), null, false));
    }

    private onGenderChange(genderLookupId: string): void {
        // revalidateNAM when state has been set with new gender value
        this.mutateRelativeModel(s => s.set("GenderLookupId", genderLookupId), () => this.onNAMChange(this.state.relative.get("NAMEdit"), null, false));
    }

    private onAddressSelect(addressControlDto: AddressControlDTO): void {
        this.mutateRelativeModel(s => s
            .set("CivicAddressEdit", addressControlDto.civicAddressEdit)
            .set("CityEdit", addressControlDto.cityEdit)
            .set("PostalCodeEdit", addressControlDto.postalCodeEdit)
            .set("IsPostalCodeValid", addressControlDto.isPostalCodeValid)
            .set("SelectedCountryLookupId", addressControlDto.selectedCountryLookupId)
            .set("SelectedRegionLookupId", !IsNullOrWhiteSpace(addressControlDto.selectedRegionLookupId) ? addressControlDto.selectedRegionLookupId : s.get("SelectedRegionLookupId"))
        );
    }

    private onPhone1TypeChange(val: string): void {
        this.mutateRelativeModel(s => {
            const contactPointLookupId = val.split("_")[0];
            const phoneLookupId = val.split("_")[1];

            s = s.set("Phone1TypeLookupId", phoneLookupId)
                .set("Phone1ContactTypeLookupId", contactPointLookupId);
            if (phoneLookupId !== this.cellphoneLookupId)
                s = s.set("Phone1NotificationsEnabled", false);
            return s;
        });
    }

    private onPhone2TypeChange(val: string): void {
        this.mutateRelativeModel(s => {
            const contactPointLookupId = val.split("_")[0];
            const phoneLookupId = val.split("_")[1];

            s = s.set("Phone2TypeLookupId", phoneLookupId)
                .set("Phone2ContactTypeLookupId", contactPointLookupId);
            if (phoneLookupId !== this.cellphoneLookupId)
                s = s.set("Phone2NotificationsEnabled", false);
            return s;
        });
    }

    private onEmailChange(val: string): void {
        this.mutateRelativeModel(s => s
            .set("EmailEdit", val)
            .set("IsEmailValid", this.isEmailValid(val))
        );
    }

    private isHealthCardValid(healthcard: string, regionLookupId: string): boolean {
        let relative = this.state.relative;

        if (!cacheStore.ClinicSettings.EnableHealthCardValidation) {
            const isOntarioHealthCard = regionLookupId == cacheStore.FindLookupByLookupCode("CanadaState_ON").LookupID;
            if (isOntarioHealthCard)
                return true;

            return this.validationHelper.validateNAM(
                healthcard,
                relative.get("SelectedCountryLookupId"),
                regionLookupId,
                relative.get("FirstName"),
                relative.get("LastName"),
                relative.get("DOB"),
                relative.get("GenderLookupId")
            );
        }

        const validator = HealthCardValidatorFactory.getValidator(cacheStore.GetDefaultCountryForActiveSite().LookupID, regionLookupId);
        const request = new HealthCardValidatorRequest(
            healthcard?.trim() || "",
            relative.get("FirstName"),
            relative.get("LastName"),
            relative.get("DOB"),
            relative.get("GenderLookupId")
        );

        return validator.isValid(request);
    }

    private setGenderFromNAM(val: string, currentModel: IRelativeCreationModel): IRelativeCreationModel {
        if (val.length >= 7 && RelativeCreationModel.getHealthCardRegionLookupId(currentModel) == cacheStore.FindLookupByLookupCode("CanadaState_QC").LookupID) {
            const monthFirstDigit = parseInt(val.substr(6, 1));

            if (monthFirstDigit > 1)
                currentModel = currentModel.set("GenderLookupId", this.femaleLookupId.LookupID);
            else
                currentModel = currentModel.set("GenderLookupId", this.maleLookupId.LookupID);

            if (IsNullOrWhiteSpace(currentModel.get("SelectedPrefixLookupId")))
                currentModel = currentModel.set("SelectedPrefixLookupId", monthFirstDigit > 1 ? this.femalePrefixLookupId.LookupID : this.malePrefixLookupId.LookupID);
        }

        return currentModel;
    }

    private isPhoneNumberValid(number: string): boolean {
        if (!number)
            return true;

        const phonevalidationRule: ValidationRule = this.validationHelper.getPhoneNumberValidationRule(this.state.relative.get("SelectedCountryLookupId"), null);
        const phonevalidationRegex: string = phonevalidationRule ? phonevalidationRule.ValidationRegexPattern : "";

        if (phonevalidationRegex) {
            const regexp = new RegExp(phonevalidationRegex, "i");
            return ValidationHelper.isExactMatch(number, regexp);
        }

        return true;
    }

    private isEmailValid(email: string): boolean {
        if (!email)
            return true;

        return ValidationHelper.isExactMatch(email, ValidationHelper.emailRegex);
    }

    private isFormValid(): boolean {
        const model = this.state.relative;
        const isFirstNameValid: boolean = !!model.get("FirstName");
        const isLastNameValid: boolean = !!model.get("LastName");
        const isNAMValid: boolean = this.isHealthCardValid(model.get("NAMEdit"), model.get("SelectedHealthCardRegionLookupId"));
        const isPrimaryNumberValid: boolean = this.isPhoneNumberValid(model.get("Phone1Edit"));
        const isSecondaryNumberValid: boolean = this.isPhoneNumberValid(model.get("Phone2Edit"));
        const isEmailValid: boolean = this.isEmailValid(model.get("EmailEdit"));
        return isFirstNameValid && isLastNameValid && isNAMValid && isPrimaryNumberValid && isSecondaryNumberValid && isEmailValid;
    }

    private async validateIsNotDuplicate(healthCardNumber: string, regionLookupId: string): Promise<void> {
        if (this.alreadyDuplicateCheckedNam === healthCardNumber)
            return;

        this.alreadyDuplicateCheckedNam = healthCardNumber;

        const duplicateIndividualsIds = await IndividualApiClient.getDuplicatesAsync(healthCardNumber, regionLookupId);

        const s = new CreateRelativeState();
        s.relative = this.state.relative;

        let followUp;

        if (!isArrayNullOrUndefinedOrEmpty(duplicateIndividualsIds) && healthCardNumber) {
            s.duplicateCreatedIndividualContactIds = duplicateIndividualsIds.map(p => p.Id);

            if (!s.displayDuplicatePopup) {
                s.displayDuplicatePopup = true;
            }
        }
        else {
            if (this.state.relative.get("SelectedRegionLookupId") == cacheStore.FindLookupByLookupCode("CanadaState_QC").LookupID)
                followUp = this.getIndividualNameAtRamq(healthCardNumber);

            s.duplicateCreatedIndividualContactIds = [];
        }

        if (followUp != null)
            this.setState(s, followUp);
        else
            this.setState(s);
    }

    private getIndividualNameAtRamq(nam: string): void {
        let self = this;

        if (self.alreadyResolvedNam == nam)
            return;

        self.alreadyResolvedNam = nam;

        const requestData = {
            Patient: { NAM: nam },
            CurrentUser: sessionStore.getCurrentEmployee().getRamqCredentials(),
            ForceB2BPatientNameResolve: true
        };

        const s = new CreateRelativeState();
        s.isLoadingRAMQ = true;
        self.setState(s, async () => {
            const response = await Client.postAsync<RAMQInfo>(ServerOperations.Server.RAMQ_CheckSubscription, requestData);
            const s = new CreateRelativeState();

            if (response.errors?.any()) {
                s.isLoadingRAMQ = false;
                return self.setState(s);
            }

            const ramqInfo: RAMQInfo = response.data;

            s.relative = this.state.relative;

            if (!IsNullOrWhiteSpace(ramqInfo.FirstName) || !IsNullOrWhiteSpace(ramqInfo.LastName)) {
                if (IsNullOrWhiteSpace(self.state.relative.get("LastName")) && !IsNullOrWhiteSpace(ramqInfo.LastName)) {
                    s.relative = s.relative.set("LastName", ramqInfo.LastName);
                }

                if (IsNullOrWhiteSpace(self.state.relative.get("FirstName")) && !IsNullOrWhiteSpace(ramqInfo.LastName)) {
                    s.relative = s.relative.set("FirstName", ramqInfo.FirstName);
                }
            }

            s.isLoadingRAMQ = false;
            self.setState(s);
        });
    }

    private parseToRelative(): Relative {
        return Relative.FromRelativeCreationModel(this.state.relative);
    }
}

export { CreateRelative };
